import React from 'react';
import { useNavigate } from 'react-router-dom';

interface PageHeaderProps {
  /** Page title displayed next to the back button */
  title: string;
  /** Page subtitle/description */
  subtitle?: string;
  /**
   * Navigation target when back button is clicked.
   * If not provided, uses navigate(-1) for browser history back.
   */
  backTo?: string;
  /** Custom back button label for accessibility */
  backLabel?: string;
  /** Optional right-side action slot */
  action?: React.ReactNode;
}

/**
 * Unified page header component for expert portal pages.
 * Provides consistent back navigation and page title styling.
 *
 * Usage:
 * ```tsx
 * <PageHeader title="Services" backTo="/app" />
 * <PageHeader title="Service Details" subtitle="Edit your service" />
 * <PageHeader
 *   title="Clients"
 *   backTo="/app"
 *   action={<AddButton />}
 * />
 * ```
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  backTo,
  backLabel = 'Go back',
  action,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="group flex items-center justify-center w-10 h-10 -ml-2 rounded-xl text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 active:scale-95 flex-shrink-0"
            aria-label={backLabel}
          >
            <svg
              className="w-5 h-5 transition-transform duration-200 group-hover:-translate-x-0.5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Title */}
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">{title}</h1>
            {subtitle && (
              <p className="mt-1 text-sm text-gray-600">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Optional Action Slot */}
        {action && (
          <div className="flex-shrink-0">
            {action}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
